/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Pencil, Plus, Trash2 } from 'lucide-react';
import { type JSX, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type ServiceItem } from '../../lib/api/finance.js';
import { useDeleteServiceItem, useServiceItems } from '../../lib/queries/finance.js';
import { formatInr } from '../../lib/utils/money.js';

import { ServiceItemForm } from './service-item-form.js';

interface RowProps {
  readonly item: ServiceItem;
  readonly onEdit: (item: ServiceItem) => void;
  readonly onDelete: (id: string) => void;
  readonly disabled: boolean;
}

function CatalogueRow({ item, onEdit, onDelete, disabled }: RowProps): JSX.Element {
  const { t } = useTranslation();
  const edit = useCallback(() => {
    onEdit(item);
  }, [onEdit, item]);
  const remove = useCallback(() => {
    onDelete(item.id);
  }, [onDelete, item.id]);
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium text-slate-800 dark:text-slate-100">{item.name}</p>
          {!item.active && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
              {t('finance.catalogue.inactive')}
            </span>
          )}
        </div>
        {item.description !== null && (
          <p className="truncate text-sm text-slate-500 dark:text-slate-400">{item.description}</p>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="font-semibold text-slate-700 dark:text-slate-200">
          {formatInr(item.unitPricePaise)}
        </span>
        <button
          type="button"
          onClick={edit}
          aria-label={t('finance.catalogue.edit')}
          className="focus-ring rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={remove}
          disabled={disabled}
          aria-label={t('finance.catalogue.delete')}
          className="focus-ring rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/** Finance → Service catalogue / fee schedule (BRD M10 — P1-9). */
export function FinanceCataloguePage(): JSX.Element {
  const { t } = useTranslation();
  const items = useServiceItems();
  const del = useDeleteServiceItem();
  const [editing, setEditing] = useState<ServiceItem | null>(null);
  const [open, setOpen] = useState(false);

  const list = items.data ?? [];
  const openAdd = useCallback(() => {
    setEditing(null);
    setOpen(true);
  }, []);
  const openEdit = useCallback((item: ServiceItem) => {
    setEditing(item);
    setOpen(true);
  }, []);
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  const onDelete = useCallback(
    (id: string) => {
      del.mutate(id);
    },
    [del],
  );

  return (
    <div className="p-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <PageHeader
          title={t('finance.catalogue.title')}
          subtitle={t('finance.catalogue.count', { count: list.length })}
        />
        <Button onClick={openAdd}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('finance.catalogue.add')}
        </Button>
      </div>
      <QueryState
        isLoading={items.isLoading}
        isError={items.isError}
        isEmpty={list.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('finance.catalogue.empty')}
      >
        <div className="space-y-3">
          {list.map((item) => (
            <CatalogueRow
              key={item.id}
              item={item}
              onEdit={openEdit}
              onDelete={onDelete}
              disabled={del.isPending}
            />
          ))}
        </div>
      </QueryState>
      <ServiceItemForm open={open} onOpenChange={setOpen} item={editing} onSaved={close} />
    </div>
  );
}
